import { useQueryClient } from '@tanstack/react-query';
import { Ban, FilePen, Loader2, NotebookPen, Paperclip, PenLine, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { ProntuarioResponse } from '@/api/generated/model';
import {
  getProntuariosControllerListarQueryKey,
  prontuariosControllerCriarRascunho,
  prontuariosControllerDescartarRascunho,
  useProntuariosControllerAssinar,
  useProntuariosControllerCancelar,
  useProntuariosControllerListar,
} from '@/api/generated/prontuarios/prontuarios';
import { ApiError } from '@/api/http-client';
import { AnexosDoProntuario, ItensDoProntuario } from '@/features/historico/components/AnexosDoProntuario';
import { ConsultaGravada } from '@/features/historico/components/ConsultaGravada';
import { NovoAnexoDialogo } from '@/features/historico/components/NovoAnexoDialogo';
import { ProntuarioFormDialogo } from '@/features/historico/components/ProntuarioFormDialogo';
import {
  CLASSE_STATUS_PRONTUARIO,
  formatarDataHora,
  ROTULO_STATUS_PRONTUARIO,
  ROTULO_TIPO,
} from '@/features/historico/rotulos';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';

/** Mesmo mínimo do servidor (`cancelarProntuarioSchema`). */
const MOTIVO_MINIMO = 5;

/**
 * Aba de prontuário — a que a V1 tinha (`ProntuarioTab.tsx`) e a V2 não.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Desde o item 16 o prontuário é IMUTÁVEL depois de registrado: não há
 *  editar nem excluir. Atualização entra como ANEXO — e só o prontuário mais
 *  recente do paciente aceita (`aceitaAnexo`, calculado pelo servidor; a tela
 *  não deduz). Engano se resolve CANCELANDO, com motivo.
 *
 *  "Novo prontuário" cria um RASCUNHO na hora (ele recebe fotos, exames e a
 *  gravação durante a consulta). Rascunho não é registro: fica numa seção à
 *  parte, "Em andamento", com Continuar/Descartar, e não expira.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function AbaProntuario({ pacienteId }: { pacienteId: string }) {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [rascunhoAberto, setRascunhoAberto] = useState<ProntuarioResponse | null>(null);
  const [criandoRascunho, setCriandoRascunho] = useState(false);
  const [aDescartar, setADescartar] = useState<ProntuarioResponse | null>(null);
  const [descartando, setDescartando] = useState(false);
  const [aAnexar, setAAnexar] = useState<ProntuarioResponse | null>(null);
  const [aAssinar, setAAssinar] = useState<ProntuarioResponse | null>(null);
  const [aCancelar, setACancelar] = useState<ProntuarioResponse | null>(null);
  const [motivo, setMotivo] = useState('');

  const { data, isPending, isError, refetch } = useProntuariosControllerListar({
    pacienteId,
    pagina: 1,
    porPagina: 100,
    ordenarPor: 'atendidoEm',
    // Atendimento mais recente primeiro: o default do schema é `asc`, e abrir a
    // ficha na consulta mais antiga é o avesso do que se procura.
    ordem: 'desc',
  });

  const podeCriar = pode('prontuarios.criar');

  const rascunhos = useProntuariosControllerListar(
    { pacienteId, status: 'RASCUNHO', pagina: 1, porPagina: 20, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: podeCriar } },
  );

  const assinar = useProntuariosControllerAssinar();
  const cancelar = useProntuariosControllerCancelar();

  const podeAssinar = pode('prontuarios.editar');
  // Cancelar substituiu o excluir — mesma permissão.
  const podeCancelar = pode('prontuarios.excluir');

  async function invalidar() {
    await queryClient.invalidateQueries({ queryKey: getProntuariosControllerListarQueryKey() });
  }

  /** O rascunho nasce no clique — é ele que recebe arquivos, exames e gravação desde o primeiro minuto. */
  async function novoProntuario() {
    if (criandoRascunho) return;
    setCriandoRascunho(true);
    try {
      const criado = await prontuariosControllerCriarRascunho({ pacienteId });
      setRascunhoAberto(criado);
      await invalidar();
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível abrir o prontuário.');
    } finally {
      setCriandoRascunho(false);
    }
  }

  async function confirmarDescarte(rascunho: ProntuarioResponse) {
    setDescartando(true);
    try {
      await prontuariosControllerDescartarRascunho(rascunho.id);
      await invalidar();
      toast.success('Rascunho descartado.');
      setADescartar(null);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível descartar o rascunho.');
    } finally {
      setDescartando(false);
    }
  }

  async function confirmarAssinatura(prontuario: ProntuarioResponse) {
    try {
      await assinar.mutateAsync({ id: prontuario.id });
      await invalidar();
      toast.success('Prontuário assinado.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível assinar.');
    } finally {
      setAAssinar(null);
    }
  }

  async function confirmarCancelamento(prontuario: ProntuarioResponse) {
    try {
      await cancelar.mutateAsync({ id: prontuario.id, data: { motivo: motivo.trim() } });
      await invalidar();
      toast.success('Prontuário cancelado.');
      setACancelar(null);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível cancelar.');
    }
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {podeCriar && (
          <div className="flex justify-end">
            <Button onClick={() => void novoProntuario()} disabled={criandoRascunho}>
              {criandoRascunho ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Novo prontuário
            </Button>
          </div>
        )}

        {podeCriar && (rascunhos.data?.itens.length ?? 0) > 0 && (
          <section
            aria-label="Prontuários em andamento"
            className="rounded-lg border-2 border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
          >
            <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400">
              <FilePen className="w-4 h-4" /> Em andamento
              <span className="font-normal text-muted-foreground">— rascunhos ainda não registrados</span>
            </p>
            <ul className="space-y-2">
              {rascunhos.data!.itens.map((rascunho) => (
                <li
                  key={rascunho.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/20 bg-background/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {rascunho.titulo || (ROTULO_TIPO[rascunho.tipo] ?? rascunho.tipo)}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      Iniciado em {formatarDataHora(rascunho.criadoEm)} · {rascunho.veterinarioNome}
                      {rascunho.conteudo ? ` · ${rascunho.conteudo.slice(0, 80)}${rascunho.conteudo.length > 80 ? '…' : ''}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      aria-label={`Descartar rascunho de ${formatarDataHora(rascunho.criadoEm)}`}
                      onClick={() => setADescartar(rascunho)}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Descartar
                    </Button>
                    <Button size="sm" onClick={() => setRascunhoAberto(rascunho)}>
                      Continuar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6">
          {isPending ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-24 rounded-md" />
              ))}
            </div>
          ) : isError || !data ? (
            <EstadoErro aoTentarDeNovo={() => void refetch()} />
          ) : data.itens.length === 0 ? (
            <EstadoVazio
              icone={NotebookPen}
              titulo="Nenhum prontuário"
              descricao="Registre a primeira consulta deste paciente."
            />
          ) : (
            <ul className="space-y-4">
              {data.itens.map((prontuario) => {
                const cancelado = prontuario.status === 'CANCELADO';
                const quando = formatarDataHora(prontuario.atendidoEm);
                return (
                  <li
                    key={prontuario.id}
                    className={`rounded-lg border p-4 space-y-2 ${cancelado ? 'border-destructive/20 opacity-75' : 'border-brand/15'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">{ROTULO_TIPO[prontuario.tipo] ?? prontuario.tipo}</Badge>
                          <Badge variant="outline" className={CLASSE_STATUS_PRONTUARIO[prontuario.status]}>
                            {ROTULO_STATUS_PRONTUARIO[prontuario.status] ?? prontuario.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{quando}</span>
                        </div>

                        {prontuario.titulo && <p className="font-medium text-foreground mt-1.5">{prontuario.titulo}</p>}
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {podeCriar && prontuario.aceitaAnexo && (
                          <Button variant="outline" size="sm" onClick={() => setAAnexar(prontuario)}>
                            <Paperclip className="w-3.5 h-3.5" /> Adicionar anexo
                          </Button>
                        )}

                        {podeAssinar && prontuario.status === 'REGISTRADO' && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Assinar prontuário de ${quando}`}
                                onClick={() => setAAssinar(prontuario)}
                              >
                                <PenLine className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Assinar</TooltipContent>
                          </Tooltip>
                        )}

                        {podeCancelar && !cancelado && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Cancelar prontuário de ${quando}`}
                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  setMotivo('');
                                  setACancelar(prontuario);
                                }}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Cancelar</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {cancelado && (
                      <p className="rounded-md bg-destructive/5 px-3 py-1.5 text-xs text-destructive">
                        Cancelado{prontuario.canceladoEm ? ` em ${formatarDataHora(prontuario.canceladoEm)}` : ''}
                        {prontuario.canceladoPorNome ? ` por ${prontuario.canceladoPorNome}` : ''}
                        {prontuario.motivoCancelamento ? ` — ${prontuario.motivoCancelamento}` : ''}
                      </p>
                    )}

                    <p className="text-sm text-muted-foreground whitespace-pre-line">{prontuario.conteudo}</p>

                    <ItensDoProntuario prontuarioId={prontuario.id} arquivos={prontuario.arquivos} exames={prontuario.exames} />

                    {prontuario.gravacaoConsulta && (
                      <ConsultaGravada
                        gravacaoId={prontuario.gravacaoConsulta.id}
                        duracaoSegundos={prontuario.gravacaoConsulta.duracaoSegundos}
                      />
                    )}

                    <p className="text-xs text-muted-foreground">
                      {prontuario.veterinarioNome}
                      {prontuario.totalVersoes > 0 &&
                        ` · ${prontuario.totalVersoes === 1 ? '1 versão anterior' : `${prontuario.totalVersoes} versões anteriores`}`}
                    </p>

                    <AnexosDoProntuario prontuarioId={prontuario.id} totalAnexos={prontuario.totalAnexos} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Um diálogo por rascunho (`key`): montado de novo, o salvamento automático
            parte do estado DESTE rascunho — nunca do formulário do anterior. */}
        {rascunhoAberto && (
          <ProntuarioFormDialogo
            key={rascunhoAberto.id}
            aberto
            aoFechar={() => setRascunhoAberto(null)}
            pacienteId={pacienteId}
            rascunho={rascunhoAberto}
          />
        )}

        <AlertDialog open={Boolean(aDescartar)} onOpenChange={(v) => !v && setADescartar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Descartar este rascunho?</AlertDialogTitle>
              <AlertDialogDescription>
                O rascunho é apagado de vez — com o texto, as fotos/PDFs e a gravação da consulta. Os exames
                vinculados continuam no cadastro do paciente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={descartando}
                onClick={() => aDescartar && void confirmarDescarte(aDescartar)}
              >
                {descartando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Descartar rascunho
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {aAnexar && <NovoAnexoDialogo aberto aoFechar={() => setAAnexar(null)} prontuario={aAnexar} />}

        <AlertDialog open={Boolean(aAssinar)} onOpenChange={(v) => !v && setAAssinar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Assinar este prontuário?</AlertDialogTitle>
              <AlertDialogDescription>
                A assinatura fica registrada no prontuário. Atualizações posteriores continuam entrando como anexo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction onClick={() => aAssinar && void confirmarAssinatura(aAssinar)}>Assinar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={Boolean(aCancelar)} onOpenChange={(v) => !v && setACancelar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar este prontuário?</AlertDialogTitle>
              <AlertDialogDescription>
                Use quando ele foi registrado por engano (paciente errado, duplicado). O prontuário continua visível,
                marcado como cancelado, com o motivo, quem cancelou e quando — e deixa de aceitar anexos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Motivo do cancelamento"
              aria-label="Motivo do cancelamento"
              className={CLASSE_CONTROLE}
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={motivo.trim().length < MOTIVO_MINIMO || cancelar.isPending}
                onClick={() => aCancelar && void confirmarCancelamento(aCancelar)}
              >
                Cancelar prontuário
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
